/* «Заполнить из описания» (APP-SPEC-5 §11 п. 4, §13.1 п. 4; docs/03-api.md §7.6 POST /app/infog/extract):
   описание товара → название, преимущества и характеристики только по фактам текста. Бесплатно, без заказа.
   Пока Backend не публикует метод, лист честно говорит, что разбор недоступен, и ничего не придумывает. */
import { useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { TextField } from '../../ui/TextField';
import { StudioSheet } from '../studio/StudioSheet';
import { EXTRACT_EMPTY, EXTRACT_MIN, extractEmpty, extractFailure, extractProduct, type ExtractFound as Found } from './product-extract';
export function ProductExtractSheet({
  kind,
  disabled,
  onFill,
}: {
  kind: 'infog' | 'mcard';
  disabled?: boolean;
  onFill(found: Found): void;
}) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [found, setFound] = useState<Found | null>(null);
  const [marks, setMarks] = useState<Set<string>>(new Set());
  const minimum = EXTRACT_MIN[kind];
  const close = () => {
    setOpen(false);
    setFound(null);
    setError('');
  };
  async function extract() {
    if (busy || Array.from(description.trim()).length < minimum) return;
    setBusy(true);
    setError('');
    try {
      const result = await extractProduct(description, kind);
      if (extractEmpty(result)) {
        setError(EXTRACT_EMPTY);
        return;
      }
      if (kind === 'mcard') {
        onFill(result);
        close();
        return;
      }
      setFound(result);
      setMarks(new Set([...result.advantages.map((_, index) => `a${index}`), ...result.specs.map((_, index) => `s${index}`)]));
    } catch (failure) {
      setError(extractFailure(failure));
    } finally {
      setBusy(false);
    }
  }
  const picked = found
    ? {
        name: found.name,
        advantages: found.advantages.filter((_, index) => marks.has(`a${index}`)),
        specs: found.specs.filter((_, index) => marks.has(`s${index}`)),
      }
    : null;
  const total = picked ? picked.advantages.length + picked.specs.length : 0;
  const toggle = (id: string) =>
    setMarks(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <>
      <button type="button" className="lnk px-fill" disabled={disabled} onClick={() => setOpen(true)}>
        {uiText('Заполнить из описания')}
      </button>
      {open && (
        <StudioSheet
          title="Заполнить из описания"
          onClose={close}
          footer={
            found && picked ? (
              <button
                type="button"
                className={`btn${total ? '' : ' off'}`}
                aria-disabled={!total || undefined}
                onClick={() => {
                  if (!total) return;
                  onFill(picked);
                  close();
                }}
              >
                {uiFormat('Добавить {0}', total)}
              </button>
            ) : (
              <button
                type="button"
                className={`btn${busy || Array.from(description.trim()).length < minimum ? ' off' : ''}`}
                aria-disabled={busy || Array.from(description.trim()).length < minimum || undefined}
                onClick={() => void extract()}
              >
                {uiText(busy ? 'Ищем…' : kind === 'infog' ? 'Найти преимущества' : 'Заполнить')}
              </button>
            )
          }
        >
          <h2>{uiText('Заполнить из описания')}</h2>
          <p className="hint">{uiText('Возьмём название и короткие преимущества')}</p>
          {!found && (
            <>
              <TextField label={uiText('Описание товара')} value={description} limit={3000} minimum={minimum} disabled={busy} onChange={setDescription} />
              <p className="hint">{uiText('Цены и призывы не возьмём')}</p>
            </>
          )}
          {found && (
            <div className="px-found">
              <b>{uiText('Что нашли')}</b> <span className="hint">{uiText('Отметьте, что добавить')}</span>
              {found.name && <p>{found.name}</p>}
              {found.advantages.length > 0 && (
                <fieldset>
                  <legend>{uiFormat('Преимущества · поместится {0}', 6)}</legend>
                  {found.advantages.map((item, index) => (
                    <label key={item}>
                      <input type="checkbox" checked={marks.has(`a${index}`)} onChange={() => toggle(`a${index}`)} /> {item}
                    </label>
                  ))}
                </fieldset>
              )}
              {found.specs.length > 0 && (
                <fieldset>
                  <legend>{uiFormat('Характеристики · поместится {0}', 8)}</legend>
                  {found.specs.map(([key, item], index) => (
                    <label key={key}>
                      <input type="checkbox" checked={marks.has(`s${index}`)} onChange={() => toggle(`s${index}`)} /> {key} · {item}
                    </label>
                  ))}
                </fieldset>
              )}
            </div>
          )}
          {error && <p role="alert">{uiText(error)}</p>}
        </StudioSheet>
      )}
    </>
  );
}
